import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

import { useCan, Privilege } from '@feedflo/access-control';

import Button, { Colors, Variants } from '../../atoms/Button';
import ComboboxMulti from '../../atoms/ComboboxMulti';
import Select from '../../atoms/Select';
import TextInput from '../../atoms/TextInput';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import TypeToConfirmDialog from '../../organisms/TypeToConfirmDialog';
import useSearchParamsState from '../../utils/hooks/useSearchParamsState';

const AS_USER_ROLE = { headers: { 'x-hasura-role': 'user' } };

const GET_DEVICES = gql`
  query DeviceFirmwareTableQuery {
    device(where: { deleted_at: { _is_null: true } }, order_by: { serial: asc }) {
      id
      serial
      label
      hw_version
      type
      firmware_branch {
        id
        name
      }
      device_assignments(
        where: { deleted_at: { _is_null: true }, ended_at: { _is_null: true } }
        limit: 1
      ) {
        feed_line {
          name
          farm {
            id
            name
            organization {
              name
            }
          }
        }
      }
      bin_controller {
        bin_set {
          farm {
            id
            name
            organization {
              name
            }
          }
        }
      }
    }
  }
`;

const GET_FIRMWARE_BRANCHES = gql`
  query GetActiveFirmwareBranchesQuery {
    firmware_branch(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
      id
      name
      dangerous_to_deploy
      firmwares(
        where: { deleted_at: { _is_null: true } }
        order_by: { created_at: desc }
        limit: 1
      ) {
        tag
      }
    }
  }
`;

const BULK_SET_DEVICE_FW_BRANCH = gql`
  mutation BulkSetDeviceFWBranch($ids: [uuid!]!, $fwBranchId: uuid!) {
    update_device(where: { id: { _in: $ids } }, _set: { firmware_branch_id: $fwBranchId }) {
      affected_rows
    }
  }
`;

const SET_DEVICE_FW_BRANCH = gql`
  mutation SetDeviceFWBranch($deviceId: uuid!, $fwBranchId: uuid) {
    update_device(where: { id: { _eq: $deviceId } }, _set: { firmware_branch_id: $fwBranchId }) {
      affected_rows
    }
  }
`;

function getBarn(device) {
  return device.device_assignments?.[0]?.feed_line?.farm
    || device.bin_controller?.bin_set?.farm
    || null;
}

function getOrgName(device) {
  return device.device_assignments?.[0]?.feed_line?.farm?.organization?.name
    || device.bin_controller?.bin_set?.farm?.organization?.name
    || '—';
}

function getDevicesPageUrl(serials, name) {
  const params = new URLSearchParams();
  params.set('name', name ?? (serials.length === 1 ? serials[0] : `${serials.length} selected device(s)`));
  params.set('sort', 'nameAsc');
  serials.forEach((serial) => params.append('d', serial));
  return `/devices?${params.toString()}`;
}

function getDevicePageUrl(serial) {
  return getDevicesPageUrl([serial], serial);
}

function formatTagDisplay(tag) {
  if (tag === null || tag === undefined) return 'None';
  return tag;
}

const SEARCH_DEBOUNCE_MS = 500;

function DeviceFirmwareFilterField({ className = '', clearLabel, showClear, onClear, children }) {
  return (
    <div className={`DeviceFirmwareController-filterField ${className}`.trim()}>
      {children}
      {showClear && (
        <button
          type="button"
          className="DeviceFirmwareController-filterClear"
          aria-label={clearLabel}
          onClick={onClear}
        >
          ×
        </button>
      )}
    </div>
  );
}

DeviceFirmwareFilterField.propTypes = {
  className: PropTypes.string,
  clearLabel: PropTypes.string.isRequired,
  showClear: PropTypes.bool.isRequired,
  onClear: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const DeviceFirmwareTableRow = React.memo(function DeviceFirmwareTableRow({
  device,
  barn,
  isStaff,
  isSelected,
  branchSelectItems,
  onToggle,
  onBranchChange,
}) {
  return (
    <tr className={isSelected ? 'DeviceFirmwareController-row--selected' : ''}>
      {isStaff && (
        <td>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(device.id)}
          />
        </td>
      )}
      <td className="DeviceFirmwareController-serial">
        <Link
          to={getDevicePageUrl(device.serial)}
          target="_blank"
          rel="noopener noreferrer"
          className="DeviceFirmwareController-tableLink"
        >
          {device.serial}
        </Link>
      </td>
      <td>{device.label ?? '—'}</td>
      <td>{device.type ?? '—'}</td>
      <td>{device.hw_version ?? '—'}</td>
      <td>
        {barn?.id ? (
          <Link
            to={`/b/${barn.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="DeviceFirmwareController-tableLink"
          >
            {barn.name}
          </Link>
        ) : (
          '—'
        )}
      </td>
      <td>{getOrgName(device)}</td>
      <td>
        {isStaff ? (
          <Select
            itemList={branchSelectItems}
            value={device.firmware_branch?.id ?? ''}
            defaultText="None"
            onChange={(e) => onBranchChange(device.id, e.target.value)}
          />
        ) : (
          device.firmware_branch?.name ?? 'None'
        )}
      </td>
    </tr>
  );
});

DeviceFirmwareTableRow.propTypes = {
  device: PropTypes.shape({
    id: PropTypes.string.isRequired,
    serial: PropTypes.string.isRequired,
    label: PropTypes.string,
    type: PropTypes.string,
    hw_version: PropTypes.string,
    firmware_branch: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  barn: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  isStaff: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  branchSelectItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onToggle: PropTypes.func.isRequired,
  onBranchChange: PropTypes.func.isRequired,
};

export default function DeviceFirmwareController() {
  // --- State hooks ---
  const [searchParam, setSearchParam] = useSearchParamsState('q', '');
  const [searchInput, setSearchInput] = useState(searchParam ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam ?? '');
  const [selectedBranchFilters, setSelectedBranchFilters] = useSearchParamsState('branch', []);
  const [selectedTypeFilters, setSelectedTypeFilters] = useSearchParamsState('type', []);
  const [selectedHwFilters, setSelectedHwFilters] = useSearchParamsState('hw', []);
  const [selectedOrgFilters, setSelectedOrgFilters] = useSearchParamsState('org', []);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [bulkTargetBranchId, setBulkTargetBranchId] = useState('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [individualConfirmOpen, setIndividualConfirmOpen] = useState(false);
  const [pendingBranchChange, setPendingBranchChange] = useState(null);

  // --- Atoms and other Hooks ---
  const { canAnywhere } = useCan();

  // --- Privileges and Access Hooks ---
  const isStaff = canAnywhere('feature', Privilege.FEATURE_STAFF_ACCESS);

  // --- Apollo/GraphQL hooks ---
  const { data: deviceData, loading: devicesLoading, refetch } = useQuery(GET_DEVICES, {
    context: AS_USER_ROLE,
    fetchPolicy: 'no-cache',
  });
  const { data: branchData } = useQuery(GET_FIRMWARE_BRANCHES, {
    context: AS_USER_ROLE,
    fetchPolicy: 'no-cache',
  });
  const firmwareBranches = branchData?.firmware_branch ?? [];

  const [bulkSetFwBranch] = useMutation(BULK_SET_DEVICE_FW_BRANCH, {
    context: AS_USER_ROLE,
    onCompleted: () => refetch(),
  });
  const [setDeviceFwBranch] = useMutation(SET_DEVICE_FW_BRANCH, {
    context: AS_USER_ROLE,
    onCompleted: () => refetch(),
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setSearchParam(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, setSearchParam]);

  // --- Derived state ---
  const allDevices = deviceData?.device ?? [];

  const hwVersionOptions = useMemo(() => {
    const seen = new Set();
    return allDevices
      .map((d) => d.hw_version)
      .filter((v) => v && !seen.has(v) && seen.add(v))
      .sort()
      .map((v) => ({ id: v, name: v }));
  }, [allDevices]);

  const deviceTypeOptions = useMemo(() => {
    const seen = new Set();
    return allDevices
      .map((d) => d.type)
      .filter((v) => v && !seen.has(v) && seen.add(v))
      .sort()
      .map((v) => ({ id: v, name: v }));
  }, [allDevices]);

  const orgOptions = useMemo(() => {
    const seen = new Set();
    return allDevices
      .map((d) => getOrgName(d))
      .filter((v) => v !== '—' && !seen.has(v) && seen.add(v))
      .sort()
      .map((v) => ({ id: v, name: v }));
  }, [allDevices]);

  const branchFilterOptions = useMemo(
    () => firmwareBranches.map((b) => ({ id: b.id, name: b.name })),
    [firmwareBranches],
  );

  const filteredDevices = useMemo(() => {
    const searchLower = debouncedSearch.trim().toLowerCase();
    return allDevices.filter((device) => {
      if (
        searchLower
        && !device.serial.toLowerCase().includes(searchLower)
        && !(device.label ?? '').toLowerCase().includes(searchLower)
      ) {
        return false;
      }
      if (
        selectedBranchFilters.length > 0
        && !selectedBranchFilters.map(String).includes(String(device.firmware_branch?.id ?? 'none'))
      ) {
        return false;
      }
      if (
        selectedTypeFilters.length > 0
        && !selectedTypeFilters.map(String).includes(String(device.type))
      ) {
        return false;
      }
      if (
        selectedHwFilters.length > 0
        && !selectedHwFilters.map(String).includes(String(device.hw_version))
      ) {
        return false;
      }
      if (
        selectedOrgFilters.length > 0
        && !selectedOrgFilters.map(String).includes(String(getOrgName(device)))
      ) {
        return false;
      }
      return true;
    });
  }, [allDevices, debouncedSearch, selectedBranchFilters, selectedTypeFilters, selectedHwFilters, selectedOrgFilters]);

  const allFilteredSelected = filteredDevices.length > 0 && filteredDevices.every((d) => selectedDeviceIds.has(d.id));
  const someFilteredSelected = filteredDevices.some((d) => selectedDeviceIds.has(d.id));
  const selectedCount = selectedDeviceIds.size;

  const branchSelectItems = useMemo(
    () =>
      firmwareBranches.map((b) => ({
        id: b.id,
        name: `${b.name}${b.dangerous_to_deploy ? ' ⚠ DANGEROUS' : ''}`,
      })),
    [firmwareBranches],
  );

  const branchLiveTagById = useMemo(() => {
    const map = new Map();
    firmwareBranches.forEach((branch) => {
      map.set(branch.id, branch.firmwares?.[0]?.tag ?? null);
    });
    return map;
  }, [firmwareBranches]);

  const getLiveTag = useCallback((branchId) => {
    if (!branchId) return null;
    return branchLiveTagById.get(branchId) ?? null;
  }, [branchLiveTagById]);

  const getBranchName = useCallback((branchId) => {
    if (!branchId) return 'None';
    return firmwareBranches.find((b) => b.id === branchId)?.name ?? 'Unknown';
  }, [firmwareBranches]);

  const bulkConfirmDetails = useMemo(() => {
    if (!bulkTargetBranchId) return null;

    const selectedDevices = allDevices.filter((device) => selectedDeviceIds.has(device.id));
    const bulkTargetTag = getLiveTag(bulkTargetBranchId);
    const bulkTargetBranchName = getBranchName(bulkTargetBranchId);

    let sameTagCount = 0;
    let willDownloadCount = 0;
    const tagCounts = new Map();

    selectedDevices.forEach((device) => {
      const currentTag = getLiveTag(device.firmware_branch?.id);
      const tagKey = currentTag ?? '__none__';
      tagCounts.set(tagKey, (tagCounts.get(tagKey) ?? 0) + 1);

      if (currentTag === bulkTargetTag) {
        sameTagCount += 1;
      } else {
        willDownloadCount += 1;
      }
    });

    const startingTags = Array.from(tagCounts.entries())
      .map(([key, count]) => {
        const tag = key === '__none__' ? null : key;
        return {
          tag,
          display: formatTagDisplay(tag),
          count,
          isSameAsTarget: tag === bulkTargetTag,
        };
      })
      .sort((a, b) => b.count - a.count);

    return {
      bulkTargetTag,
      bulkTargetBranchName,
      sameTagCount,
      willDownloadCount,
      startingTags,
      selectedCount: selectedDevices.length,
    };
  }, [allDevices, bulkTargetBranchId, getBranchName, getLiveTag, selectedDeviceIds]);

  const bulkConfirmMessage = useMemo(() => {
    if (!bulkConfirmDetails) return '';

    const {
      bulkTargetBranchName,
      bulkTargetTag,
      sameTagCount,
      willDownloadCount,
      startingTags,
      selectedCount,
    } = bulkConfirmDetails;

    return (
      <div className="DeviceFirmwareController-bulkConfirm">
        <p>
          Reassign {selectedCount} device(s) to <strong>{bulkTargetBranchName}</strong>.
        </p>
        <p>
          Target tag: <span className="tag">{formatTagDisplay(bulkTargetTag)}</span>
        </p>
        {startingTags.length > 0 && (
          <>
            <p><strong>Current tags:</strong></p>
            <ul className="DeviceFirmwareController-tagList">
              {startingTags.map(({ tag, display, count, isSameAsTarget }) => (
                <li
                  key={tag ?? '__none__'}
                  className={`DeviceFirmwareController-tagListItem${isSameAsTarget ? ' DeviceFirmwareController-tagListItem--same' : ''}`}
                >
                  <span className="tag">{display}</span>
                  {' — '}
                  {count} device{count !== 1 ? 's' : ''}
                  {isSameAsTarget && ' (no change)'}
                </li>
              ))}
            </ul>
          </>
        )}
        {bulkTargetTag === null ? (
          <p className="DeviceFirmwareController-tagWarningNote">
            Target branch has no live firmware. Devices will not receive firmware until one is published.
          </p>
        ) : (
          <>
            {sameTagCount > 0 && (
              <p>
                {sameTagCount} of {selectedCount} device(s) already run this tag (no new download).
              </p>
            )}
            {willDownloadCount > 0 && (
              <p>
                {willDownloadCount} device(s) will download new firmware on their next check-in.
              </p>
            )}
          </>
        )}
      </div>
    );
  }, [bulkConfirmDetails]);

  // --- Function definitions ---
  const toggleDevice = useCallback((id) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleIndividualChangeRequest = useCallback((deviceId, fwBranchId) => {
    const device = allDevices.find((d) => d.id === deviceId);
    if (!device) return;

    const currentBranchId = device.firmware_branch?.id ?? '';
    const newBranchId = fwBranchId || '';
    if (currentBranchId === newBranchId) return;

    setPendingBranchChange({
      deviceId,
      serial: device.serial,
      fwBranchId: fwBranchId || null,
      previousBranchName: getBranchName(currentBranchId),
      newBranchName: getBranchName(newBranchId),
      previousTag: getLiveTag(currentBranchId),
      newTag: getLiveTag(newBranchId),
    });
    setIndividualConfirmOpen(true);
  }, [allDevices, getBranchName, getLiveTag]);

  const handleIndividualConfirm = async () => {
    if (!pendingBranchChange) return;
    const { deviceId, fwBranchId, serial, newBranchName } = pendingBranchChange;
    setIndividualConfirmOpen(false);
    setPendingBranchChange(null);
    try {
      await setDeviceFwBranch({ variables: { deviceId, fwBranchId } });
      toast.success(`Updated "${serial}" to "${newBranchName}"`, { position: 'top-right', autoClose: 4000 });
    } catch (err) {
      toast.error('Failed to update device', { position: 'top-right' });
      Sentry.captureException(err);
    }
  };

  const handleIndividualCancel = () => {
    setIndividualConfirmOpen(false);
    setPendingBranchChange(null);
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedDeviceIds((prev) => {
        const next = new Set(prev);
        filteredDevices.forEach((d) => next.delete(d.id));
        return next;
      });
    } else {
      setSelectedDeviceIds((prev) => {
        const next = new Set(prev);
        filteredDevices.forEach((d) => next.add(d.id));
        return next;
      });
    }
  };

  const handleBulkConfirm = async () => {
    setBulkConfirmOpen(false);
    try {
      const ids = Array.from(selectedDeviceIds);
      await bulkSetFwBranch({ variables: { ids, fwBranchId: bulkTargetBranchId } });
      const branchName = firmwareBranches.find((b) => b.id === bulkTargetBranchId)?.name;
      toast.success(`Updated ${ids.length} device(s) to "${branchName}"`, { position: 'top-right', autoClose: 4000 });
      setSelectedDeviceIds(new Set());
    } catch (err) {
      toast.error('Failed to bulk update devices', { position: 'top-right' });
      Sentry.captureException(err);
    }
  };

  const handleOpenAllSelected = () => {
    const serials = allDevices
      .filter((d) => selectedDeviceIds.has(d.id))
      .map((d) => d.serial)
      .sort((a, b) => a.localeCompare(b));
    if (serials.length === 0) return;
    window.open(getDevicesPageUrl(serials), '_blank', 'noopener,noreferrer');
  };

  const clearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParam(undefined);
  };

  const clearBranchFilters = () => setSelectedBranchFilters(undefined);
  const clearTypeFilters = () => setSelectedTypeFilters(undefined);
  const clearHwFilters = () => setSelectedHwFilters(undefined);
  const clearOrgFilters = () => setSelectedOrgFilters(undefined);

  // --- Render ---
  if (devicesLoading) return <span>Loading devices…</span>;

  return (
    <div className="DeviceFirmwareController">
      <div className="DeviceFirmwareController-filters">
        <DeviceFirmwareFilterField
          className="DeviceFirmwareController-filterField--search"
          clearLabel="Clear search"
          showClear={searchInput.trim().length > 0}
          onClear={clearSearch}
        >
          <TextInput
            placeholder="Search serial or label…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </DeviceFirmwareFilterField>
        <DeviceFirmwareFilterField
          className="DeviceFirmwareController-filterField--combobox"
          clearLabel="Clear FW branch filter"
          showClear={selectedBranchFilters.length > 0}
          onClear={clearBranchFilters}
        >
          <ComboboxMulti
            label="FW Branch"
            items={branchFilterOptions}
            value={selectedBranchFilters}
            onChange={(items) => setSelectedBranchFilters(items.map((i) => i.id))}
            emptyPlaceholder="All branches"
            itemNameSingular="branch"
            itemNamePlural="branches"
          />
        </DeviceFirmwareFilterField>
        <DeviceFirmwareFilterField
          className="DeviceFirmwareController-filterField--combobox"
          clearLabel="Clear device type filter"
          showClear={selectedTypeFilters.length > 0}
          onClear={clearTypeFilters}
        >
          <ComboboxMulti
            label="Device Type"
            items={deviceTypeOptions}
            value={selectedTypeFilters}
            onChange={(items) => setSelectedTypeFilters(items.map((i) => i.id))}
            emptyPlaceholder="All types"
            itemNameSingular="type"
            itemNamePlural="types"
          />
        </DeviceFirmwareFilterField>
        <DeviceFirmwareFilterField
          className="DeviceFirmwareController-filterField--combobox"
          clearLabel="Clear HW version filter"
          showClear={selectedHwFilters.length > 0}
          onClear={clearHwFilters}
        >
          <ComboboxMulti
            label="HW Version"
            items={hwVersionOptions}
            value={selectedHwFilters}
            onChange={(items) => setSelectedHwFilters(items.map((i) => i.id))}
            emptyPlaceholder="All versions"
            itemNameSingular="version"
            itemNamePlural="versions"
          />
        </DeviceFirmwareFilterField>
        <DeviceFirmwareFilterField
          className="DeviceFirmwareController-filterField--combobox"
          clearLabel="Clear organization filter"
          showClear={selectedOrgFilters.length > 0}
          onClear={clearOrgFilters}
        >
          <ComboboxMulti
            label="Organization"
            items={orgOptions}
            value={selectedOrgFilters}
            onChange={(items) => setSelectedOrgFilters(items.map((i) => i.id))}
            emptyPlaceholder="All orgs"
            itemNameSingular="org"
            itemNamePlural="orgs"
          />
        </DeviceFirmwareFilterField>
      </div>

      {isStaff && someFilteredSelected && (
        <div className="DeviceFirmwareController-bulkBar">
          <span className="DeviceFirmwareController-selectionCount">{selectedCount} selected</span>
          <Button
            content="Open all devices"
            variant={Variants.Pastel}
            color={Colors.Secondary}
            onClick={handleOpenAllSelected}
          />
          <Select
            label=""
            itemList={branchSelectItems}
            value={bulkTargetBranchId}
            defaultText="Change FW branch to…"
            onChange={(e) => setBulkTargetBranchId(e.target.value)}
          />
          <Button
            content="Apply to selected"
            variant={Variants.Vivid}
            color={Colors.Warning}
            disabled={!bulkTargetBranchId}
            onClick={() => setBulkConfirmOpen(true)}
          />
        </div>
      )}

      <div className="DeviceFirmwareController-tableWrap">
        <table className="DeviceFirmwareController-table">
          <thead>
            <tr>
              {isStaff && (
                <th>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(el) => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                    onChange={toggleAll}
                  />
                </th>
              )}
              <th>Serial</th>
              <th>Label</th>
              <th>Type</th>
              <th>HW Version</th>
              <th>Barn</th>
              <th>Organization</th>
              <th>FW Branch</th>
            </tr>
          </thead>
          <tbody>
            {filteredDevices.map((device) => (
              <DeviceFirmwareTableRow
                key={device.id}
                device={device}
                barn={getBarn(device)}
                isStaff={isStaff}
                isSelected={selectedDeviceIds.has(device.id)}
                branchSelectItems={branchSelectItems}
                onToggle={toggleDevice}
                onBranchChange={handleIndividualChangeRequest}
              />
            ))}
          </tbody>
        </table>
        {filteredDevices.length === 0 && (
          <p className="DeviceFirmwareController-empty">No devices match the current filters.</p>
        )}
      </div>

      <ConfirmationDialog
        open={individualConfirmOpen}
        confirmColor={Colors.Warning}
        onCancel={handleIndividualCancel}
        onConfirm={handleIndividualConfirm}
        message={
          pendingBranchChange ? (
            <div>
              <p>
                Change FW branch for <strong>{pendingBranchChange.serial}</strong>?
              </p>
              <p>
                <strong>{pendingBranchChange.previousBranchName}</strong>
                {' → '}
                <strong>{pendingBranchChange.newBranchName}</strong>
              </p>
              <p>
                <span className="tag">{formatTagDisplay(pendingBranchChange.previousTag)}</span>
                {' → '}
                <span className="tag">{formatTagDisplay(pendingBranchChange.newTag)}</span>
              </p>
              {pendingBranchChange.previousTag === pendingBranchChange.newTag ? (
                <p className="DeviceFirmwareController-tagSameNote">
                  Same firmware tag — device will not download new firmware.
                </p>
              ) : pendingBranchChange.newTag === null ? (
                <p className="DeviceFirmwareController-tagWarningNote">
                  Target branch has no live firmware. Device will not receive firmware until one is published.
                </p>
              ) : (
                <p>The device will receive the new firmware on its next check-in.</p>
              )}
            </div>
          ) : ''
        }
      />

      <TypeToConfirmDialog
        open={bulkConfirmOpen}
        count={selectedCount}
        title="Change FW Branch for Selected Devices"
        message={bulkConfirmMessage}
        confirmLabel="Reassign Devices"
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkConfirm}
      />
    </div>
  );
}
